import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { emailPattern, phonePattern } from '@utils/patterns';
import { makeSelectOption } from '@utils/makeSelectOptions';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useMyQuery } from '@hooks/useMyQuery';
import { useS3ImageUploader } from '@hooks/useS3ImageUploader';
import { useEntityFormMaxHeight } from '@hooks/useEntityFormMaxHeight';
import { PointsTransFields } from '@pages/Points/types';
import { pointsTranslations } from '@pages/Points/translations';
import { Button } from '@components/Core/Button';
import { ImageUploader } from '@components/Core/ImageUploader/ImageUploader';
import Popup from '@components/Core/Popup/Popup';
import { SECOND_POPUP_WIDTH } from '@components/Core/Popup/constants';
import { EntityStatusRow } from '@components/Core/EntityStatusRow';
import { Input } from '@inputs/Input/Input';
import { SelectComponent } from '@inputs/SelectComponent/SelectComponent';
import { ILocationData, LocationAutocomplete } from '@inputs/LocationAutocomplete';
import { TextAreaInput } from '@inputs/TextAreaInput';
import { ConfirmationPopup } from '@popups/ConfirmationPopup';
import { StatusPopup } from '@popups/StatusPopup';
import StarIcon from '@icons/star_required_icon.svg?component';
import { DEFAULT_NAME_MIN_LENGTH } from '@hooks/useValidation';
import { ILocation } from '@/types/common';
import { IPointsCategoryData } from '@stores/points/types';
import { PointFormTransFields, IPointFormWithExtras, IPointFormProps } from './types';
import { pointFormTranslations } from './translations';

const PointForm: FC<IPointFormProps> = ({ point, brandId }) => {
  const {
    alertStore: { showAlert },
    pointsStore: {
      getPointsCategories,
      pointsCategories,
      addPoint,
      editPoint,
      changeStatus,
      shouldUpdatePointsCategories,
    },
    heremapsStore: { getAddress },
    profileStore: { user },
    mainStore: { selectedCountryCode },
    popupStore: { openPopup, closePopup, popupName },
  } = useStore();

  const [_, setSearchParams] = useSearchParams();

  const useFormMethods = useForm<IPointFormWithExtras>({
    defaultValues: {
      ...point,
      brandId,
      addressData: point
        ? makeSelectOption(point.address, { label: 'name', value: 'placeId' })
        : undefined,
    },
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { isDirty },
  } = useFormMethods;

  console.log(!pointsCategories || shouldUpdatePointsCategories);

  const {} = useMyQuery({
    queryKey: ['getPointsCategories'],
    queryFn: () => getPointsCategories(),
    enabled: !pointsCategories || shouldUpdatePointsCategories,
    staleTime: 0,
  });

  /** Check if the selected location's country code matches the brand's country code */
  const checkCountryCode = useCallback(
    (cc: string) => {
      if (cc !== selectedCountryCode?.countryCode) {
        showAlert(
          `Incorrect location was selected.\nYou should select location in the same country that your brand is`,
          'error',
          5000
        );
        setError('addressData', { type: 'value', message: 'Incorrect location was selected' });
        return false;
      }
      return true;
    },
    [selectedCountryCode]
  );

  const { mutate: getAddressMutation } = useMutation({
    mutationFn: getAddress,
    mutationKey: ['getAddress'],
    onSuccess: (res) => {
      if (!res) {
        showAlert('Getting address data problems was occured', 'error');
        return;
      }
      if (checkCountryCode(res.countryCode)) {
        setValue('countryCode', res.countryCode);
        setValue('zipCode', res.postalCode || '');
        setValue('city', res.city || '');
      }
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: addPoint,
    mutationKey: ['addPoint'],
    onSuccess: () => {
      showAlert('Point was added succesfully', 'success');
      navigate(`/brands/${brandId}/points`);
    },
  });

  const { mutate: editPointMutation, isPending: editPointPending } = useMutation({
    mutationFn: editPoint,
    mutationKey: ['editPoint'],
    onSuccess: () => {
      showAlert('Point was changed succesfully', 'success');
      navigate(`/brands/${brandId}/points`);
    },
  });

  const { mutate: changeStatusMutation, isPending: changeStatusPending } = useMutation({
    mutationFn: changeStatus,
    mutationKey: ['changeStatus'],
    onSuccess: (data) => {
      showAlert('Brand status was changed succesfully', 'success');
      if (data) setSearchParams({ status: data.status });
    },
  });

  const { t } = useMyTranslations<PointFormTransFields | Extract<PointsTransFields, 'addPoint'>>([
    pointFormTranslations.name,
    pointsTranslations.name,
  ]);

  const navigate = useNavigate();

  useEffect(() => {
    if (point?.adminMessage) {
      // need to set Timeout beacouse of rendering problems with title prop at MsgActionModal
      setTimeout(() => openPopup('status'));
    }
  }, [point?.adminMessage]);

  const afterLocationSelect = useCallback(
    async (value: ILocationData | null, data?: { position: ILocation }) => {
      setValue('address', value?.name || '');
      if (data) {
        setValue('latitude', data.position.lat);
        setValue('longitude', data.position.lng);
        getAddressMutation({ lat: data.position.lat, lng: data.position.lng });
      } else {
        setValue('latitude', '');
        setValue('longitude', '');
        setValue('zipCode', '');
        setValue('city', '');
      }
    },
    []
  );

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<IPointFormWithExtras>(
    (val) => {
      if (point) editPointMutation({ ...point, ...val });
      else mutate(val);
    }
  );

  const sendRequest = useCallback(
    async (formData: IPointFormWithExtras) => {
      const { imageFile } = formData;
      if (imageFile)
        // if image was added or changed firstly upload image to recieve it's url
        await uploadImage(formData, imageFile, 'image', `bussiness-profile/${user?.id}/points`);
      else {
        if (point) editPointMutation({ ...point, ...formData });
        else mutate(formData);
      }
      closePopup();
    },
    [point, user]
  );

  const onSubmit = useCallback(
    (formData: IPointFormWithExtras) => {
      if (!checkCountryCode(formData.countryCode)) return;
      if (point) sendRequest(formData);
      else openPopup('confirmation');
    },
    [point]
  );

  const renderSubmitBtn = useCallback(
    (place: string) => (
      <Button
        text={point ? t('save') : t('addPoint', { ns: pointsTranslations.name })}
        onClick={handleSubmit(onSubmit)}
        disabled={
          isImageUploading || isPending || editPointPending || changeStatusPending || !isDirty
        }
        styleProp={`max-w-[170px] sm-brand-form-submit ${
          place === 'inside' ? 'md-brand-form-submit-hide' : 'md-brand-form-submit-show'
        }`}
      />
    ),
    [isImageUploading, isPending, editPointPending, changeStatusPending, point, isDirty]
  );

  const renderCategoriesOption = useCallback(
    (
      { backgroundColor, borderColor, categoryImage, categoryName }: IPointsCategoryData,
      useItemColor = true
    ) => {
      // TODO use .light while there is no other theme
      const style = useItemColor
        ? {
            backgroundColor: backgroundColor.light,
            border: `1px solid ${borderColor.light}`,
          }
        : undefined;
      return (
        <div
          className="flex items-center gap-x-[8px] w-fit py-[2px] pr-[8px] pl-[4px] rounded-[6px]"
          style={style}
        >
          <img src={categoryImage} width={16} height={16} />
          <p className="small-text">{categoryName}</p>
        </div>
      );
    },
    []
  );

  const maxHeight = useEntityFormMaxHeight();
  return (
    // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore
    <>
      <FormProvider {...useFormMethods}>
        <form
          id="form"
          className="flex gap-[32px] px-[48px] pb-[48px] sm-page-padding md-brand-form-right overflow-scroll"
          style={{ maxHeight }}
        >
          <section className="section">
            <div className="bg-white flex flex-col shadow-sm p-[24px] rounded-[16px] gap-y-[24px] w-[640px] md-brand-form-inputs">
              <h6 className="subtitle !mb-0 !uppercase">{t('basic')}</h6>
              <Input
                name="name"
                type="text"
                required
                placeholder={t('name')}
                showRequiredIcon
                label={t('name')}
                minLength={DEFAULT_NAME_MIN_LENGTH}
              />
              <TextAreaInput
                name="description"
                placeholder={t('description')}
                maxLength={140}
                label={t('description')}
              />
              <Input
                name="phone"
                type="text"
                placeholder={t('phone')}
                registerPattern={phonePattern}
                label={t('phone')}
              />
              <Input
                name="email"
                type="text"
                placeholder={t('email')}
                registerPattern={emailPattern}
                label={t('email')}
              />
              <SelectComponent
                name="categories"
                placeholder={t('categories')}
                options={pointsCategories || []}
                optionLabel="categoryName"
                optionValue="id"
                required
                isMulti
                customOption={renderCategoriesOption}
                customMultiValue={(data) => renderCategoriesOption(data, false)}
                label={t('categories')}
                showRequiredIcon
                multiValueRemoveOptions={{ strokeDataKey: 'borderColor' }}
                multiValueStyleOptions={{
                  backgroundColorDataKey: 'backgroundColor',
                  borderColorDataKey: 'borderColor',
                }}
              />
              <div className="flex items-center gap-x-[10px]">
                <h6 className="subtitle !mb-0 !uppercase">{t('location')}</h6>
                <StarIcon width={12} height={12} />
              </div>
              <LocationAutocomplete
                name="addressData"
                placeholder={t('address')}
                afterSelect={afterLocationSelect}
                useOwnDataAsValue
                required
              />
              {point && (
                <EntityStatusRow
                  status={point.status}
                  onChange={(status) => changeStatusMutation({ id: point.id, status })}
                  disabled={
                    isImageUploading || isPending || editPointPending || changeStatusPending
                  }
                />
              )}
            </div>
            {renderSubmitBtn('inside')}
          </section>

          <section className="section md-brand-form-left sm-brand-form-left">
            <ImageUploader name="image" title={t('image')} />
          </section>
          {renderSubmitBtn('outside')}
        </form>
      </FormProvider>
      <Popup width={SECOND_POPUP_WIDTH}>
        {popupName === 'confirmation' && (
          <ConfirmationPopup
            title={t('addPoint', { ns: pointsTranslations.name })}
            onAccept={() => sendRequest(getValues())}
            text={t('confirmationText')}
          />
        )}
        {popupName === 'status' && point?.adminMessage && (
          <StatusPopup status="Revise" text={point?.adminMessage} />
        )}
      </Popup>
    </>
  );
};

export default observer(PointForm);
